/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { patientApi } from '../lib/api';
import { patientStorage } from '../lib/patientStorage';
import { capturePatientSession } from '../lib/api/sessionToken';
import { PatientPortalProfile } from '../types';
import { decodeJwtExpiryMs } from '../lib/jwtExpiry';

interface PatientAuthContextType {
  patient: PatientPortalProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const PatientAuthContext = createContext<PatientAuthContextType | undefined>(undefined);

// Mirrors AuthContext's staff-side silent-refresh timer — see that file for the full rationale.
const REFRESH_BUFFER_MS = 2 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;
const MAX_REFRESH_DELAY_MS = 6 * 60 * 60 * 1000;

export function PatientAuthProvider({ children }: { children: React.ReactNode }) {
  const [patient, setPatient] = useState<PatientPortalProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Silently extends the patient session before the access token expires, via the
   * patient_refresh_token httpOnly cookie — mirrors AuthContext.scheduleTokenRefresh on the staff
   * side. patientApi.auth.refresh() updates patientStorage directly, the single source of truth
   * every part of the patient portal reads the token from.
   */
  const scheduleTokenRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiryMs = decodeJwtExpiryMs(token);
    if (expiryMs == null) return;

    const rawDelay = expiryMs - Date.now() - REFRESH_BUFFER_MS;
    const delay = Math.min(Math.max(rawDelay, MIN_REFRESH_DELAY_MS), MAX_REFRESH_DELAY_MS);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await patientApi.auth.refresh();
        if (response.error) return; // fetchPatientApi's own 401 handling covers redirect-to-login
        const newToken = capturePatientSession(response);
        if (newToken) scheduleTokenRefresh(newToken);
      } catch {
        // Refresh failed — let the existing 401-on-next-request handling take over.
      }
    }, delay);
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const initialize = async () => {
      if (patientStorage.hasActiveSession()) {
        try {
          const response = await patientApi.auth.getCurrentPatient();
          const responseData = (response.data as { data?: { patient?: PatientPortalProfile } } | undefined)?.data || response.data;
          if (responseData?.patient) {
            setPatient(responseData.patient);
            const token = patientStorage.getAuthToken();
            if (token) scheduleTokenRefresh(token);
          } else {
            patientStorage.removeAuthToken();
          }
        } catch {
          patientStorage.removeAuthToken();
        }
      }
      setLoading(false);
    };

    initialize();
  }, [scheduleTokenRefresh]);

  const signIn = async (email: string, password: string) => {
    const response = await patientApi.auth.login(email, password);
    if (response.error) {
      throw new Error(response.error);
    }

    const responseData = (response.data as { data?: { token?: string; patient?: PatientPortalProfile } } | undefined)?.data || response.data;
    if (!responseData?.token || !responseData?.patient) {
      throw new Error('Invalid response from server');
    }

    capturePatientSession(response);
    setPatient(responseData.patient);
    scheduleTokenRefresh(responseData.token);
  };

  const signOut = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    patientStorage.removeAuthToken();
    // Don't let a clinic-switch choice leak into the next login on a shared device.
    sessionStorage.removeItem('active_patient_clinic_id');
    setPatient(null);
    // Best-effort — revokes the refresh-token family server-side; client state is already
    // cleared above regardless of whether this call succeeds.
    patientApi.auth.logout().catch(() => undefined);
    window.location.href = '/patient/login';
  };

  const refresh = async () => {
    const response = await patientApi.auth.getCurrentPatient();
    if (response.error) {
      throw new Error(response.error);
    }

    const responseData = (response.data as { data?: { patient?: PatientPortalProfile } } | undefined)?.data || response.data;
    if (responseData?.patient) {
      setPatient(responseData.patient);
    }
  };

  return (
    <PatientAuthContext.Provider value={{ patient, loading, signIn, signOut, refresh }}>
      {children}
    </PatientAuthContext.Provider>
  );
}

export function usePatientAuth() {
  const context = useContext(PatientAuthContext);
  if (!context) {
    throw new Error('usePatientAuth must be used within PatientAuthProvider');
  }
  return context;
}
